import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, Circle } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Card, Typography, Spin, Alert, Button, Space } from 'antd'
import { BiMapPin, BiCurrentLocation } from 'react-icons/bi'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

// eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

interface Coordinates {
  latitude: number
  longitude: number
}

interface LocationPinSelectorProps {
  initialCoordinates?: Coordinates
  accommodationAddress?: string
  onLocationChange: (coordinates: Coordinates) => void
  disabled?: boolean
  height?: number
  radius?: number // in kilometers
}

function MapClickHandler({ onLocationChange }: { onLocationChange: (coords: Coordinates) => void }) {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng
      onLocationChange({ latitude: lat, longitude: lng })
    },
  })
  return null
}

function MapCenterController({ center }: { center: Coordinates }) {
  const map = useMap()

  useEffect(() => {
    map.setView([center.latitude, center.longitude], map.getZoom())
  }, [center, map])

  return null
}

function MapZoomController({ center, radius }: { center: Coordinates; radius: number }) {
  const map = useMap()

  useEffect(() => {
    if (radius && radius > 0) {
      try {
        const radiusInMeters = radius * 1000

        const latOffset = (radiusInMeters / 111320)
        const lngOffset = radiusInMeters / (111320 * Math.cos(center.latitude * Math.PI / 180))

        const bounds = L.latLngBounds([
          [center.latitude - latOffset, center.longitude - lngOffset],
          [center.latitude + latOffset, center.longitude + lngOffset]
        ])

        map.fitBounds(bounds, { padding: [20, 20] })
      } catch (error) {
        map.setView([center.latitude, center.longitude], 13)
      }
    }
  }, [center, radius, map])

  return null
}

class GeocodingService {
  static async geocodeAddress(address: string): Promise<Coordinates | null> {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      )
      const data = await response.json()

      if (data && data.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        }
      }
      return null
    } catch (error) {
      return null
    }
  }
}

function LocationPinSelector(props: LocationPinSelectorProps) {
  const {
    initialCoordinates,
    accommodationAddress,
    onLocationChange,
    disabled = false,
    height = 400,
    radius
  } = props
  const [coordinates, setCoordinates] = useState<Coordinates>(
    initialCoordinates || { latitude: -33.8688, longitude: 151.2093 } // Sydney
  )
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [geocodingError, setGeocodingError] = useState<string | null>(null)
  const [hasGeocodedAddress, setHasGeocodedAddress] = useState(false)

  useEffect(() => {
    if (initialCoordinates) {
      return
    }

    if (accommodationAddress && !hasGeocodedAddress) {
      setIsGeocoding(true)
      setGeocodingError(null)

      GeocodingService.geocodeAddress(accommodationAddress)
        .then((coords) => {
          if (coords) {
            setCoordinates(coords)
            onLocationChange(coords)
            setHasGeocodedAddress(true)
          } else {
            setGeocodingError('Could not find location for the provided address. Please use current location or click on the map.')
          }
        })
        .catch(() => {
          setGeocodingError('Failed to geocode address. Please use current location or click on the map.')
        })
        .finally(() => {
          setIsGeocoding(false)
        })
    } else if (!accommodationAddress && !hasGeocodedAddress && navigator.geolocation) {
      setIsGeocoding(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
          setCoordinates(coords)
          onLocationChange(coords)
          setHasGeocodedAddress(true)
          setIsGeocoding(false)
        },
        () => {
          setGeocodingError('Could not get your location. Please click on the map to set your notification area.')
          setIsGeocoding(false)
        }
      )
    }
  }, [accommodationAddress, hasGeocodedAddress, initialCoordinates, onLocationChange])

  const handleLocationChange = (newCoords: Coordinates) => {
    setCoordinates(newCoords)
    onLocationChange(newCoords)
  }

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
          handleLocationChange(coords)
        },
        (_error) => {
          setGeocodingError('Could not get your current location')
        }
      )
    } else {
      setGeocodingError('Geolocation is not supported by this browser')
    }
  }

  const handleRetryGeocode = () => {
    if (accommodationAddress) {
      setHasGeocodedAddress(false)
      setGeocodingError(null)
    }
  }

  return (
    <Card
      title={
        <Space>
          <BiMapPin />
          <Typography.Text strong>Select Notification Location</Typography.Text>
        </Space>
      }
      extra={
        <Button
          icon={<BiCurrentLocation />}
          onClick={handleUseCurrentLocation}
          disabled={disabled}
          size="small"
        >
          Use Current Location
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          Click on the map to set your preferred notification location. This will be used as the center point for radius-based incident alerts.
        </Typography.Paragraph>

        {geocodingError && (
          <Alert
            message="Location Error"
            description={geocodingError}
            type="warning"
            showIcon
            action={
              accommodationAddress && (
                <Button size="small" onClick={handleRetryGeocode}>
                  Retry
                </Button>
              )
            }
          />
        )}

        <div style={{ position: 'relative', height: `${height}px`, width: '100%' }}>
          {isGeocoding && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
            >
              <Spin size="large" />
            </div>
          )}

          <MapContainer
            center={[coordinates.latitude, coordinates.longitude]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={!disabled}
            dragging={!disabled}
            touchZoom={!disabled}
            doubleClickZoom={!disabled}
            boxZoom={!disabled}
            keyboard={!disabled}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <Marker
              position={[coordinates.latitude, coordinates.longitude]}
              draggable={!disabled}
              eventHandlers={{
                dragend: (e) => {
                  const marker = e.target
                  const position = marker.getLatLng()
                  handleLocationChange({
                    latitude: position.lat,
                    longitude: position.lng
                  })
                },
              }}
            />

            {radius && radius > 0 && (
              <Circle
                center={[coordinates.latitude, coordinates.longitude]}
                radius={radius * 1000}
                pathOptions={{
                  color: '#1890ff',
                  fillColor: '#1890ff',
                  fillOpacity: 0.1,
                  weight: 2,
                  dashArray: '5, 5'
                }}
              />
            )}

            {!disabled && <MapClickHandler onLocationChange={handleLocationChange} />}
            <MapCenterController center={coordinates} />
            {radius && <MapZoomController center={coordinates} radius={radius} />}
          </MapContainer>
        </div>

        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
          <BiMapPin />&nbsp;
          Current location: {coordinates.latitude.toFixed(6)}, {coordinates.longitude.toFixed(6)}
          {accommodationAddress && hasGeocodedAddress && (
            <span> (auto-located from: {accommodationAddress})</span>
          )}
        </Typography.Text>
      </Space>
    </Card>
  )
}

LocationPinSelector.defaultProps = {
  initialCoordinates: undefined,
  accommodationAddress: undefined,
  disabled: false,
  height: 400,
  radius: undefined,
}

export default LocationPinSelector
